import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { doCreateData, doDeleteData, doGetAuditsData, doGetData, doSearch, doUpdateData, emptyRole, luHandleAppiError, type U2102Role } from "../U2102Service";
import { useU2102Context } from "../U2102Context";
import { NS, U2102ROLES } from "../U2102";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2102RoleDeleteDlg from "./common/U2102RoleDeleteDlg";
import U2102RoleBody from "./form/u2102Role/U2102RoleBody";
import { U2102RoleFormToolBar } from "./form/u2102Role/U2102RoleFormToolBar";
import U2102RoleActionsBody from "./form/u2102Role/U2102RoleActionTab/U2102RoleActionBody";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U2102RoleFormExtendedHandlers extends U2000FormStateHandlersReturn<U2102Role> {
  addNewRole: () => void;
  modifyRole: (role: U2102Role) => void;
  deleteRole: (id: string) => Promise<void>;
}

const U2102RoleForm: React.FC = () => {
  // Parameters
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u2101LogicalUnitBL
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2102Context();

  // Form Handlers and default methods
  const validateForm = (role: U2102Role) => {
    const result: Record<string, string> = {};
    if (!role.roleCode) result.roleCode = t("errRoleCodeRequired", { ns: NS });
    if (!role.roleDescription) {
      result.roleDescription = t("errRoleDescriptionRequired", { ns: NS });
    }
    return result;
  };

  const u2102RoleBL: U2000FormSBusinessHandlers<U2102Role> = {
    newRecord: () => {
      return emptyRole;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (role: U2102Role, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(role);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(role, editMode);
      } else {
        u2102RoleFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const role = u2102RoleFormStateExtendedHandlers.getOldData() as U2102Role | null;
      if (role) {
        // Met à false checkedId pour chaque action
        if ("roleActions" in role && Array.isArray((role as any).roleActions)) {
          (role as any).roleActions = (role as any).roleActions.map((action: any) => ({ ...action, idChecked: false }));
        }
        u2102RoleformStateHandlers.setData({ ...role });
      }

      if (luStateHandlers.getActionOriginFrom() === "/U2102") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (role) => {
      if (role.id) {
        handleGet.getData(role.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U2102");
    },
  };

  const u2102RoleformStateHandlers = useU2000FormStateHandlers<U2102Role>({
    formState,
    formStateDispatch,
    businessHandlers: u2102RoleBL,
  });

  // extended methods
  const u2102RoleFormStateExtendedHandlers: U2102RoleFormExtendedHandlers = {
    ...u2102RoleformStateHandlers,

    addNewRole: () => {
      u2102RoleformStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyRole: () => {
      u2102RoleformStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteRole: async (_id: string) => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U2102Role>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U2102Role>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U2102Role & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (logicalUnit: U2102Role & { id: string }) => {
      gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      u2102RoleFormStateExtendedHandlers.setData(logicalUnit);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U2102", label: t("title", { ns: NS }) }, { label: logicalUnit.roleDescription + " (" + logicalUnit.roleCode + ")" }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u2102RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U2102Role>({
    doSaveFn: (logicalUnit: U2102Role, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, logicalUnit);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, logicalUnit.id, logicalUnit);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (logicalUnit: U2102Role & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), logicalUnit]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(logicalUnit.id, logicalUnit);
      }
      u2102RoleFormStateExtendedHandlers.afterSaveData(logicalUnit);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U2102") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u2102RoleFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u2102RoleFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u2102RoleFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["roleCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U2102", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U2102/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u2102RoleFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u2102RoleFormStateExtendedHandlers.setData({ ...emptyRole });
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabAction",
      label: t("tabActions", { ns: NS }),
      content: <U2102RoleActionsBody u2102RoleFormStateExtendedHandlers={u2102RoleFormStateExtendedHandlers} />,
    },

    hasRole(U2102ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u2102RoleFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U2102RoleAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U2102RoleDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U2102RoleFormToolBar formStateHandlers={u2102RoleFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u2102RoleFormStateExtendedHandlers as any}
      >
        <U2102RoleBody formStateHandlers={u2102RoleFormStateExtendedHandlers as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabAction"
          activeTab={activeTab ?? "tabAction"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U2102RoleForm;
